// Restmengen je Bestellposition (Plan 2026-10-19): bestellt / verschifft / offen ueber alle
// Container. Die Rechnung kommt fertig vom Server (Akte `restmengen`); hier nur Anzeige und
// das bewusste Ausweisen eines Rests (Event rest_abschluss, Recht Einkauf). Kein Geld-Pfad.
import { useState } from "react";
import { toast } from "sonner";
import type { Lagereinheit, OrderRestmengen, PurchaseOrderFile, RestPosition, RestStatus } from "@/api/purchase";
import { useRestAbschluss } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fehlerText } from "@/lib/fehlerText";
import { mengeMitEinheit } from "./einheit";
import { containerNummern, containerTitel, spurFarbe } from "./arbeitsplatz/schritte";

export interface ContainerKennung {
  titel: string;
  farbe: string;
}

/** Container-Titel und -Farbe wie im Arbeitsplatz (Nummer nach Bezeichnung, Farbe nach Reihenfolge der Spuren, sonst der Akte). */
export function containerKennungen(order: PurchaseOrderFile): Map<number, ContainerKennung> {
  const reihenfolge = order.arbeitsplatz?.modus === "spuren"
    ? order.arbeitsplatz.spuren.map((s) => s.shipment_id)
    : order.shipments.map((s) => s.id);
  for (const s of order.shipments) if (!reihenfolge.includes(s.id)) reihenfolge.push(s.id);
  const nummern = containerNummern(order.shipments, reihenfolge);
  const out = new Map<number, ContainerKennung>();
  reihenfolge.forEach((id, i) => {
    out.set(id, { titel: containerTitel(order.shipments.find((s) => s.id === id), nummern.get(id)!), farbe: spurFarbe(i) });
  });
  return out;
}

const STATUS_CHIP: Record<RestStatus, string> = {
  offen: "border-warn/40 bg-warn-soft text-warn",
  vollstaendig: "border-success/40 bg-success-soft text-success",
  ueberliefert: "border-coral/40 bg-coral-soft text-coral",
  abgeschlossen: "border-line bg-panel-2 text-ink-2",
};

function statusText(rest: RestPosition, einheit: Lagereinheit | undefined): string {
  switch (rest.status) {
    case "offen": return "Rest offen";
    case "vollstaendig": return "vollständig";
    case "ueberliefert": return `${mengeMitEinheit(rest.ueber, einheit)} überliefert`;
    case "abgeschlossen": return "Rest ausgewiesen";
  }
}

const fmtTag = (iso: string) =>
  new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });

/**
 * Verladeleiste: je Container ein Abschnitt in seiner Spurfarbe, der offene Rest als
 * gestrichelte Luecke, ein ausgewiesener Rest schraffiert. Reine Zierde zur Zahl daneben
 * (aria-hidden); die Werte stehen als Text in der Zelle.
 */
function Verladeleiste({ rest, kennung }: { rest: RestPosition; kennung: Map<number, ContainerKennung> }) {
  const basis = Math.max(rest.bestellt, rest.verschifft, 1);
  return (
    <div aria-hidden="true" className="mt-1 flex h-1.5 w-full min-w-28 overflow-hidden rounded-full">
      {rest.je_container.map((c) => (
        <span
          key={c.shipment_id}
          className="h-full border-r border-background last:border-r-0"
          style={{ width: `${(c.qty / basis) * 100}%`, background: kennung.get(c.shipment_id)?.farbe ?? "var(--ink-2)" }}
        />
      ))}
      {rest.offen > 0 ? (
        <span
          className={`h-full rounded-r-full ${rest.status === "abgeschlossen" ? "" : "border border-dashed border-warn/70"}`}
          style={{
            width: `${(rest.offen / basis) * 100}%`,
            ...(rest.status === "abgeschlossen"
              ? { background: "repeating-linear-gradient(135deg, var(--line) 0 3px, transparent 3px 6px)" }
              : {}),
          }}
        />
      ) : null}
    </div>
  );
}

/** Zelle „verschifft“: Summe, Leiste, Aufteilung je Container. */
export function VerschifftZelle({ rest, einheit, kennung }: {
  rest: RestPosition | undefined; einheit: Lagereinheit | undefined; kennung: Map<number, ContainerKennung>;
}) {
  if (!rest) return <span className="text-ink-2">—</span>;
  const aufteilung = rest.je_container
    .map((c) => `${kennung.get(c.shipment_id)?.titel ?? `#${c.shipment_id}`}: ${mengeMitEinheit(c.qty, einheit)}`)
    .join(" · ");
  return (
    <div className="min-w-32">
      <div className="text-right font-mono">{mengeMitEinheit(rest.verschifft, einheit)}</div>
      <Verladeleiste rest={rest} kennung={kennung} />
      {aufteilung ? <div className="mt-1 text-[11px] leading-snug text-ink-2">{aufteilung}</div> : null}
    </div>
  );
}

/** Zelle „offen“: Rest, Status, Ausweisung mit Grund, Knopf zum Ausweisen bzw. Aufheben. */
export function OffenZelle({ rest, einheit, sku, darfAusweisen }: {
  rest: RestPosition | undefined; einheit: Lagereinheit | undefined; sku: string; darfAusweisen: boolean;
}) {
  if (!rest) return <span className="text-ink-2">—</span>;
  const ab = rest.abschluss;
  const veraltet = ab !== null && !ab.gueltig && rest.status === "offen";
  return (
    <div className="grid min-w-36 justify-items-end gap-1">
      <span className={`font-mono ${rest.status === "offen" ? "font-semibold text-warn" : ""}`}>{mengeMitEinheit(rest.offen, einheit)}</span>
      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_CHIP[rest.status]}`}>{statusText(rest, einheit)}</span>
      {ab && rest.status === "abgeschlossen" ? (
        <span className="max-w-56 text-right text-[11px] leading-snug text-ink-2">
          {ab.von ?? "unbekannt"} am {fmtTag(ab.am)}: „{ab.grund}“
        </span>
      ) : null}
      {veraltet ? (
        <span className="text-right text-[11px] text-ink-2">Ausweisung veraltet ({mengeMitEinheit(ab.offen, einheit)} ausgewiesen)</span>
      ) : null}
      {darfAusweisen ? <RestAusweisenKnopf rest={rest} einheit={einheit} sku={sku} /> : null}
    </div>
  );
}

/**
 * Knopf „Rest ausweisen…“ bzw. „Ausweisung aufheben…“ mit dem Dialog (Begruendung Pflicht).
 * Gemeinsam fuer den Reiter Positionen und den Arbeitsplatz (21.10.2026 „alles in der Maske“).
 */
export function RestAusweisenKnopf({ rest, einheit, sku, fokusZiel }: {
  rest: RestPosition; einheit: Lagereinheit | undefined; sku: string; fokusZiel?: string;
}) {
  const [dialog, setDialog] = useState<"abschliessen" | "aufheben" | null>(null);
  return (
    <>
      {rest.status === "offen" ? (
        <Button size="sm" variant="outline" className="h-7 text-xs" data-fokus-ziel={fokusZiel} onClick={() => setDialog("abschliessen")}>Rest ausweisen…</Button>
      ) : null}
      {rest.status === "abgeschlossen" ? (
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setDialog("aufheben")}>Ausweisung aufheben…</Button>
      ) : null}
      {dialog ? (
        <RestDialog aktion={dialog} rest={rest} einheit={einheit} sku={sku} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
}

function RestDialog({ aktion, rest, einheit, sku, onClose }: {
  aktion: "abschliessen" | "aufheben"; rest: RestPosition; einheit: Lagereinheit | undefined; sku: string; onClose: () => void;
}) {
  const mutation = useRestAbschluss();
  const [grund, setGrund] = useState("");
  const grundOk = grund.trim().length >= 3;
  const offen = mengeMitEinheit(rest.offen, einheit);
  const titel = aktion === "abschliessen" ? "Rest ausweisen" : "Ausweisung aufheben";
  const absenden = () => mutation.mutate({ id: rest.order_item_id, aktion, grund: grund.trim() }, {
    onSuccess: (res) => {
      if (res.ergebnis === "unveraendert") toast.info("Der Rest war schon so ausgewiesen.");
      else toast.success(aktion === "abschliessen" ? "Rest ausgewiesen" : "Ausweisung aufgehoben");
      onClose();
    },
    onError: (e) => toast.error(fehlerText(e, `${titel} fehlgeschlagen`)),
  });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titel} · {sku}</DialogTitle>
          <DialogDescription>
            {aktion === "abschliessen"
              ? `Bestellt ${mengeMitEinheit(rest.bestellt, einheit)}, auf Container verteilt ${mengeMitEinheit(rest.verschifft, einheit)}. Die offenen ${offen} kommen nicht mehr; die Position zählt danach als erledigt. Steigt die Bestellmenge später, ist sie wieder offen.`
              : `Die Position zählt danach wieder mit ${offen} als offen.`}
          </DialogDescription>
        </DialogHeader>
        <label htmlFor={`rest-grund-${rest.order_item_id}`} className="text-sm font-medium">Begründung</label>
        <Textarea
          id={`rest-grund-${rest.order_item_id}`}
          value={grund}
          onChange={(e) => setGrund(e.target.value)}
          maxLength={500}
          rows={2}
          placeholder={aktion === "abschliessen" ? "z. B. Lieferant streicht den Rest" : "z. B. Lieferant liefert doch nach"}
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!grundOk || mutation.isPending} onClick={absenden}>{titel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Hinweis: Container-Zeilen ohne Bestellposition zaehlen bei keiner Position. */
export function UnzugeordnetHinweis({ restmengen, kennung }: { restmengen: OrderRestmengen | undefined; kennung: Map<number, ContainerKennung> }) {
  const u = restmengen?.unzugeordnet;
  if (!u || u.zeilen === 0) return null;
  const wo = u.shipment_ids.map((id) => kennung.get(id)?.titel ?? `#${id}`).join(", ");
  return (
    <p role="note" className="mt-3 rounded-md border border-warn/40 bg-warn-soft px-3 py-2 text-xs text-ink">
      {u.zeilen} {u.zeilen === 1 ? "Containerposition" : "Containerpositionen"} ({u.qty.toLocaleString("de-DE")})
      {u.zeilen === 1 ? " ist" : " sind"} keiner Bestellposition zugeordnet: {wo}.
      {" "}Sie zählen bei keiner Position mit; ist die Ware dort enthalten, den Rest der Position bewusst ausweisen.
    </p>
  );
}
